import { ReactNode } from 'react';
import { SkipNav } from '@cmsgov/design-system';

import './PageLayout.css';

export interface PageLayoutProps {
  /** Content for the left sidebar (optional - if not provided, main content takes full width) */
  aside?: ReactNode;
  /** Main content area */
  children: ReactNode;
  /** Optional alert to display at the top of the page, spanning full width */
  alert?: ReactNode;
  /** Optional page title to display at the very top, above aside and content */
  pageTitle?: string;
  /** Optional aria-label for the aside element */
  asideAriaLabel?: string;
  /** Optional ID reference for aria-labelledby on the article element */
  mainAriaLabelledby?: string;
  /** Optional ID for the article element (used for skip navigation) */
  mainId?: string;
  /** Optional tabIndex for the article element (use -1 to make skip link work with screen readers) */
  mainTabIndex?: number;
  /** Optional custom className for the row container */
  className?: string;
  /** Test ID for automated testing */
  'data-testid'?: string;
}

/**
 * Standard page layout component with aside (left column) and main content (right column)
 * Provides consistent 1104px max-width container on largest screens
 * Uses CMS Design System grid: aside is 4/3 columns, content is 8/9 columns
 */
export function PageLayout({
  aside,
  children,
  alert,
  pageTitle,
  asideAriaLabel = 'Page navigation',
  mainAriaLabelledby,
  mainId = 'content',
  mainTabIndex,
  className = '',
  'data-testid': dataTestId,
}: PageLayoutProps) {
  const baseTestId = dataTestId ?? 'page-layout';

  return (
    <>
      <SkipNav
        data-testid={`${baseTestId}-skip-nav`}
        href={`#${mainId}`}>
        Skip to page content
      </SkipNav>
      {alert && (
        <div
          className='ds-l-row'
          data-testid={`${baseTestId}-alert`}>
          <div className='ds-l-col--12'>{alert}</div>
        </div>
      )}
      {pageTitle && (
        <div
          className='ds-l-row'
          data-testid={`${baseTestId}-title-row`}>
          <div className='ds-l-col--12'>
            <h1
              className='ds-u-margin-top--0 ds-u-margin-bottom--3'
              data-testid={`${baseTestId}-title`}>
              {pageTitle}
            </h1>
          </div>
        </div>
      )}
      <div
        className={`ds-l-row page-layout ${className}`.trim()}
        data-testid={baseTestId}>
        {aside && (
          <aside
            aria-label={asideAriaLabel}
            className='ds-u-margin-bottom--4 ds-u-md-margin-bottom--0 ds-l-md-col--4 ds-l-lg-col--3'
            data-testid={`${baseTestId}-aside`}>
            {aside}
          </aside>
        )}

        <article
          aria-labelledby={mainAriaLabelledby}
          className={`ds-content ${aside ? 'ds-l-md-col--8 ds-l-lg-col--9' : 'ds-l-col--12'}`}
          data-testid={`${baseTestId}-main`}
          id={mainId}
          tabIndex={mainTabIndex}>
          {children}
        </article>
      </div>
    </>
  );
}

export default PageLayout;
